import {BufferGeometry, BufferAttribute} from 'three';

// 버스 메시. 상류(mini-tokyo-3d)는 BUS 에 `new BoxGeometry(.6, 1.2, .6)` 를 쓰는데
// 그건 UV 가 없어서 텍스처를 입힐 수 없다 (색 하나로만 칠해진다). 박스라는 선택은
// 그대로 두고 — 둥근 CAR 메시에 버스를 맞추면 UV(v=높이)가 둥근 단면에서 뭉개진다 —
// 면마다 아틀라스를 지정한 전용 지오메트리로 바꾼다. 박스는 면이 여섯 개로 딱
// 떨어지고, **지붕이 진짜 면이라 진행방향 삼각형을 제대로 그릴 수 있다.**
//
// 좌표계는 CAR 과 같다: x = 폭, y = 길이(+가 앞), z = 높이(고도축).

// 실제 시내버스는 11.0 x 2.5 x 3.2m (L/W 4.4) 인데 실비율로 두면 지도 축척에서
// 너무 길쭉하게 읽혀 길이를 줄였다 (L/W 3.1). 크기는 열차 1칸(CAR_MESH_LENGTH
// 1.584) 언저리에 둬서 지하철 자산과 나란히 놓아도 어색하지 않게 한다.
// 2026-09-04 · 09-10 · 09-11 에 각각 10% 씩 키웠다 (누적 1.33).
// **세 치수를 같은 비율로 키우면 아래 PPU 가 같은 비율로 줄어 아틀라스 픽셀 배치가
// 그대로다** - 텍스처를 다시 굽지 않아도 된다. 한 축만 키우면 UV 가 어긋난다.
export const BUS_MESH_WIDTH = 0.559;
export const BUS_MESH_LENGTH = 1.730;
export const BUS_MESH_HEIGHT = 0.718;

// ── 텍스처 아틀라스 배치 (가로 u, 세로 v. v=1 이 위) ────────────────────────
// ⚠️ **tools/make-bus-texture.py 가 같은 공식을 쓴다. 한쪽만 바꾸면 텍스처가 면을
// 벗어난다.** 상수를 양쪽에 베끼지 않고 공식을 베끼는 이유는, 치수를 바꿨을 때
// 한쪽만 갱신되는 사고를 줄이기 위해서다 (어긋나면 화면에 바로 보인다).
//
// 밴드를 손으로 정하지 않는 이유: 면마다 px/유닛이 달라져 **원이 타원이 된다.**
// 실제로 앞면 헤드라이트가 세로로 1.75배 늘어나고 옆면 바퀴는 1.24배 납작했다.
// PPU 하나로 깔면 캔버스 1px 이 어느 면에서나 같은 실치수다.
//
//   입면(옆·앞·뒤)  위쪽 v 밴드   u = 옆(길이) · 앞(폭) · 뒤(폭)
//   지붕            그 아래       u = 길이, v = 폭
//   바닥            남은 아래쪽
const TEX_W = 2048, TEX_H = 1024;
// 좌우 측면이 **각자 밴드**를 쓴다 (실차는 오른쪽에만 문이 있다). 그래서 가로
// 분모가 2L+2W 다. tools/make-bus-texture.py 가 같은 식을 쓴다.
const PPU = Math.min(TEX_W / (2 * BUS_MESH_LENGTH + 2 * BUS_MESH_WIDTH),
                     TEX_H / (BUS_MESH_HEIGHT + BUS_MESH_WIDTH));

const ELEV = [1 - BUS_MESH_HEIGHT * PPU / TEX_H, 1];
const ROOF = [ELEV[0] - BUS_MESH_WIDTH * PPU / TEX_H, ELEV[0]];
const FLOOR = [0, ROOF[0]];
const U_SIDE = [0, BUS_MESH_LENGTH * PPU / TEX_W];                  // 오른쪽(문)
const U_SIDE_L = [U_SIDE[1], U_SIDE[1] + BUS_MESH_LENGTH * PPU / TEX_W];  // 왼쪽(창만)
const U_FRONT = [U_SIDE_L[1], U_SIDE_L[1] + BUS_MESH_WIDTH * PPU / TEX_W];
const U_BACK = [U_FRONT[1], U_FRONT[1] + BUS_MESH_WIDTH * PPU / TEX_W];

// 밴드 안의 비율 위치. tools/make-bus-texture.py 의 inv() 와 같은 식이다.
const inv = (band, f) => band[0] + (band[1] - band[0]) * f;

// ── 노선번호 글리프 (2026-09-11 · 2026-09-14 두 줄) ───────────────────────
// 지붕 밴드의 **오른쪽 남는 u** 에 글리프 37칸(0~9 · '-' · A~Z)을 굽는다
// (tools/make-bus-texture.py 의 GLYPH_* 와 같은 식이다). 노선별 텍스처를 굽지
// 않는 이유: 노선이 바뀔 때마다 재생성해야 하고, 그러면 텍스처가 피드에 묶인다.
// 셰이더가 인스턴스마다 '어느 글자를 어디에' 를 정한다.
//
// 한 줄에 11칸이던 것을 **19칸 x 2줄** 로 바꿨다 ('M4101' 이 통째로 안 찍히던
// 문제). 둘째 줄은 지붕 밴드 바로 아래로, 바닥 면이 안 쓰는 u 여백에 앉는다 -
// 셰이더가 줄 번호만큼 v 를 지붕 밴드 높이만큼 내려 읽는다.
export const BUS_GLYPH_COLS = 19;
// 셰이더 uniform 으로 나간다. [지붕 u0, u1, v0, v1]
export const BUS_ROOF_BAND = [U_SIDE[0], U_SIDE[1], ROOF[0], ROOF[1]];
// [글리프 스트립 u0, 칸 너비, 한 줄 칸 수]
export const BUS_GLYPH_STRIP = [
    U_SIDE[1], (1 - U_SIDE[1]) / BUS_GLYPH_COLS, BUS_GLYPH_COLS
];

export default class extends BufferGeometry {

    constructor(width = BUS_MESH_WIDTH, length = BUS_MESH_LENGTH, height = BUS_MESH_HEIGHT) {
        super();

        const me = this,
            hw = width / 2, hl = length / 2, hh = height / 2,
            vertices = [], normals = [], uvs = [], indices = [];

        // 면 하나 = 사각형 4점. p0..p3 은 텍스처 (u0,v0)-(u1,v1) 사각형의
        // 좌하·우하·우상·좌상 순서에 대응한다. 감김은 표준 three 규약(바깥 CCW)이고
        // 재질은 FrontSide 다 — 뒤집힌 면은 컬링으로 통째로 사라지므로 아래에서
        // 검사한다.
        const quad = (p0, p1, p2, p3, n, u0, v0, u1, v1) => {
            const b = vertices.length / 3;

            for (const p of [p0, p1, p2, p3]) {
                vertices.push(...p);
                normals.push(...n);
            }
            uvs.push(u0, v0, u1, v0, u1, v1, u0, v1);
            indices.push(b, b + 2, b + 1, b, b + 3, b + 2);
        };

        // ── 단면을 길이 방향으로 밀어낸다. **박스가 아니다** - 지붕과 하부
        // 모서리를 둥글려 실차 실루엣에 가깝게 한다. 큐브는 어느 각도에서나
        // 모서리가 각져서 버스보다 컨테이너로 읽힌다.
        //
        // UV 는 밴드를 섞지 않는다: 둥근 모서리는 **측면 밴드의 맨 윗줄/맨
        // 아랫줄**(각각 차체색·스커트색인 단색 줄)을 쓰고, 평평한 지붕만 지붕
        // 밴드를 쓴다. 그래서 진행방향 삼각형이 잘리지 않는다 (평평한 폭에
        // 지붕 밴드 전체를 다시 매핑한다).
        const rt = height * .17, rb = height * .06,   // 지붕/하부 모서리 반지름
            ARC = 3,                                   // 모서리당 분할 수
            fw = hw - rt,                              // 평평한 지붕 반폭
            prof = [];

        const push = (x, z, band, v) => prof.push({x, z, band, v});
        const vSide = z => inv(ELEV, (z + hh) / height);

        // 오른쪽 아래 -> 위 -> 지붕 -> 왼쪽 -> 바닥 순서로 한 바퀴 돈다.
        push(hw, -hh + rb, ELEV, vSide(-hh + rb));
        push(hw, hh - rt, ELEV, vSide(hh - rt));
        for (let k = 1; k <= ARC; k++) {              // 지붕 오른쪽 모서리
            const a = Math.PI / 2 * k / (ARC + 1);
            push(hw - rt + rt * Math.cos(a), hh - rt + rt * Math.sin(a), ELEV, ELEV[1]);
        }
        push(fw, hh, ELEV, ELEV[1]);                  // 모서리 끝
        push(fw, hh, ROOF, inv(ROOF, 0));             // 같은 자리, 지붕 밴드로 전환
        push(-fw, hh, ROOF, inv(ROOF, 1));            // 평평한 지붕
        push(-fw, hh, ELEV, ELEV[1]);                 // 다시 측면 밴드로
        for (let k = ARC; k >= 1; k--) {              // 지붕 왼쪽 모서리
            const a = Math.PI / 2 * k / (ARC + 1);
            push(-hw + rt - rt * Math.cos(a), hh - rt + rt * Math.sin(a), ELEV, ELEV[1]);
        }
        push(-hw, hh - rt, ELEV, vSide(hh - rt));
        push(-hw, -hh + rb, ELEV, vSide(-hh + rb));
        for (let k = 1; k <= ARC; k++) {              // 하부 왼쪽 모서리
            const a = Math.PI / 2 * k / (ARC + 1);
            push(-hw + rb - rb * Math.cos(a), -hh + rb - rb * Math.sin(a), ELEV, ELEV[0]);
        }
        push(-hw + rb, -hh, ELEV, ELEV[0]);
        push(-hw + rb, -hh, FLOOR, inv(FLOOR, 1));
        push(hw - rb, -hh, FLOOR, inv(FLOOR, 0));
        push(hw - rb, -hh, ELEV, ELEV[0]);
        for (let k = ARC; k >= 1; k--) {              // 하부 오른쪽 모서리
            const a = Math.PI / 2 * k / (ARC + 1);
            push(hw - rb + rb * Math.cos(a), -hh + rb - rb * Math.sin(a), ELEV, ELEV[0]);
        }

        // 측면·지붕·바닥: 단면 선분마다 앞->뒤 사각형 하나. 밴드 전환 자리는
        // 두 점이 같은 위치라 넓이 0 이므로 건너뛴다 (감김 검사도 못 넘긴다).
        for (let k = 0; k < prof.length; k++) {
            const a = prof[k], b = prof[(k + 1) % prof.length];

            if (a.x === b.x && a.z === b.z) {
                continue;
            }
            // 바깥 법선 = 단면 진행방향을 90도 돌린 것 (한 바퀴 방향이 정해준다).
            const dx = b.x - a.x, dz = b.z - a.z,
                len = Math.hypot(dx, dz),
                n = [dz / len, 0, -dx / len],
                // 오른쪽 면은 U_SIDE, 왼쪽 면은 U_SIDE_L. 지붕·바닥은 그림이
                // 오른쪽 밴드에 있으므로 U_SIDE 를 쓴다.
                // ⚠️ **어느 밴드든 u 를 뒤집지 않는다.** 구 박스 코드는 왼쪽 면의
                // 정점 순서를 뒤로부터 감았기 때문에 u 스왑이 그 보정이었다.
                // 여기서는 모든 선분이 앞(+hl) -> 뒤(-hl) 순서라 스왑이 그대로
                // 버그가 된다 - 왼쪽에서 보면 버스가 거꾸로 달린다 (실측: 왼쪽
                // 앞 정점의 u 가 밴드 뒤끝이었다). tools/test-bus-geometry.mjs 가 본다.
                u = n[0] < -1e-6 ? U_SIDE_L : U_SIDE,
                p0 = [a.x, hl, a.z], p1 = [a.x, -hl, a.z],
                p2 = [b.x, -hl, b.z], p3 = [b.x, hl, b.z];

            quad(p0, p1, p2, p3, n, u[0], a.v, u[1], b.v);
        }

        // 앞·뒷면: 단면 다각형을 그대로 덮는다. UV 는 평면 매핑(x -> u, z -> v)이라
        // 앞면 그림(행선판·전면유리·전조등)이 제자리에 온다. 둥근 모서리에서
        // 그림이 잘리는데, 그건 실차에서도 모서리가 둥근 것과 같다.
        const capUv = (band, x, z) => [
            inv(band, (hw - x) / width), inv(ELEV, (z + hh) / height)
        ];
        // 밴드 전환 자리의 **중복 정점은 빼고** 캡을 만든다. 그대로 두면
        // 넓이 0 삼각형이 생겨 감김 검사가 터진다 (법선과의 내적이 0 이다).
        const capProf = prof.filter((q, k) => {
            const prev = prof[(k + prof.length - 1) % prof.length];

            return q.x !== prev.x || q.z !== prev.z;
        });

        for (const [sign, band, nrm] of [[1, U_FRONT, [0, 1, 0]], [-1, U_BACK, [0, -1, 0]]]) {
            const base = vertices.length / 3;

            for (const q of capProf) {
                vertices.push(q.x, hl * sign, q.z);
                normals.push(...nrm);
                const [u, v] = capUv(band, sign > 0 ? q.x : -q.x, q.z);

                uvs.push(u, v);
            }
            for (let k = 1; k < capProf.length - 1; k++) {
                indices.push(base, base + k + (sign > 0 ? 1 : 0), base + k + (sign > 0 ? 0 : 1));
            }
        }

        assertWinding(vertices, normals, indices);

        me.setIndex(indices);
        me.setAttribute('position', new BufferAttribute(new Float32Array(vertices), 3));
        me.setAttribute('normal', new BufferAttribute(new Float32Array(normals), 3));
        // 'uv' 는 three 가 USE_UV 조건으로 선언하므로 충돌을 피해 전용 이름을 쓴다
        // (car-geometry.js 와 같은 이유, 같은 이름을 공유한다).
        me.setAttribute('bodyUv', new BufferAttribute(new Float32Array(uvs), 2));
        // groupIndex 는 쓰지 않는다 - 셰이더가 #ifndef BUS 로 감싸고 있어 선언 자체가 없다.
    }

}

// 감김 1회 검사. **감김이 법선과 같은 방향(표준 CCW)이어야 통과다.**
// 재질이 FrontSide 라 뒤집힌 면은 컬링으로 통째로 사라지는데, 증상이 "그 면이
// 안 보인다" 라서 UV 문제로 오인하기 쉽다. 여기서 즉시 터뜨린다.
function assertWinding(vertices, normals, indices) {
    const at = i => [vertices[i * 3], vertices[i * 3 + 1], vertices[i * 3 + 2]];

    for (let t = 0; t < indices.length; t += 3) {
        const [a, b, c] = [at(indices[t]), at(indices[t + 1]), at(indices[t + 2])],
            e1 = b.map((v, i) => v - a[i]),
            e2 = c.map((v, i) => v - a[i]),
            cross = [
                e1[1] * e2[2] - e1[2] * e2[1],
                e1[2] * e2[0] - e1[0] * e2[2],
                e1[0] * e2[1] - e1[1] * e2[0]
            ],
            i0 = indices[t] * 3,
            dot = cross[0] * normals[i0] + cross[1] * normals[i0 + 1] + cross[2] * normals[i0 + 2];

        if (dot <= 0) {
            throw new Error(`bus-geometry: 삼각형 ${t / 3} 의 감김이 법선과 반대다. ` +
                '표준 three 규약(바깥 CCW)이어야 한다 - FrontSide 컬링이 그 면을 지운다.');
        }
    }
}
